/*
 * What a church may change about how its website looks, and what it may not.
 *
 * The standard theme fixes the layout, the typography, the spacing and the motion. A church chooses a palette, one
 * accent colour, and its logo. That is the whole surface, deliberately: the reference sites the owner chose all hold
 * their shape through weight, scale and photography rather than through hue, and a church that can set every colour
 * is a church that can make its own website unreadable. Bespoke sites are a higher tier and Milestone 5
 * (`tenants.website_tier`); nothing here applies to them.
 */

import { z } from "zod";
import { STANDARD_THEME_KEY, THEME_KEY_PATTERN } from "./theme";

/**
 * The grounds a church can pick between. Each is a complete, tested pair of surfaces and text colours, not a hue to
 * be mixed with others.
 *
 * None of them is white. Every church site the owner chose sits on an off-white: ZOE #F5EFE4, Mosaic #FAF7F1,
 * VOUS #E8E6E3, Passion City #F0F0F0. Nobody arrived there by accident.
 *
 * `quiet` is the default. Three churches told the owner they liked VOUS and Passion City best, and asked for
 * something "Apple-like, premium and simple"; both of those sit on a neutral grey rather than a cream one, and let
 * photography rather than colour carry the page.
 */
export const PALETTES = ["quiet", "warm", "mono", "dark"] as const;
export type Palette = (typeof PALETTES)[number];

export const PALETTE_LABELS: Record<Palette, string> = {
  quiet: "Quiet",
  warm: "Warm",
  mono: "Black and white",
  dark: "Dark",
};

export const PALETTE_DESCRIPTIONS: Record<Palette, string> = {
  quiet: "A soft grey ground and near-black text. Restrained, and the one most churches will want.",
  warm: "Charcoal on ivory. The same layout, a warmer paper.",
  mono: "True black on white. Plain, and the most like a printed page.",
  dark: "A dark ground throughout, for a church whose photography carries the site.",
};

/**
 * The faces a church can set its site in. Three, not thirty: a church choosing between three good faces makes a
 * better website than one choosing between a list. All three are self-hosted (`apps/public-web/public/fonts`).
 */
export const TYPEFACES = ["modern", "rounded", "editorial"] as const;
export type Typeface = (typeof TYPEFACES)[number];

export const TYPEFACE_LABELS: Record<Typeface, string> = {
  modern: "Modern",
  rounded: "Rounded",
  editorial: "Editorial",
};

export const TYPEFACE_DESCRIPTIONS: Record<Typeface, string> = {
  modern:
    "Inter Tight. Neutral and precise; the closest to the plain, premium look most churches ask for.",
  rounded: "Plus Jakarta Sans. Softer letterforms, a little warmer, still quiet.",
  editorial: "Archivo. Stronger and more headline-like, for a church whose words lead.",
};

/** A hex colour the church chose. Three or six digits, with the hash, and nothing else. */
const hexColor = z
  .string()
  .trim()
  .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "A colour looks like #1B4D3E.")
  // Stored in one canonical form, so #ABC and #aabbcc are not two different brands.
  .transform((value) => {
    const hex = value.slice(1).toLowerCase();
    return `#${hex.length === 3 ? hex.replace(/./g, (c) => c + c) : hex}`;
  });

export const brandSchema = z.object({
  /*
   * Which theme draws the site (ADR 0050). Only the shape is checked here. Whether this church may use it is the
   * database's: set_site_theme refuses a theme the church's plan does not allow, and private.site_brand leaves the
   * theme out of the envelope once the church may no longer use it, so the site reads "standard". Whether the theme
   * exists in this build is the public site's registry (`apps/public-web/src/lib/themes.ts`), which falls back too.
   */
  theme: z.string().regex(THEME_KEY_PATTERN).default(STANDARD_THEME_KEY),
  palette: z.enum(PALETTES).default("quiet"),
  /**
   * One accent, used for exactly one action per screen: the primary button, a live link, the focus ring. Everything
   * else takes its colour from the palette. Kept to one because a page with three accents has none.
   */
  /*
   * The default reads as text on the default palette: 4.70:1 on warm, 5.38:1 on white. No single accent can clear
   * 4.5:1 on both a light and a dark ground — darkening it for one dims it for the other — so a church that chooses
   * the dark palette will want a lighter accent, and the editor says so rather than leaving it to be discovered.
   */
  accent: hexColor.default("#b04a42"),
  typeface: z.enum(TYPEFACES).default("modern"),
  /** The church's logo, from its image library. Absent means the church's name is set in type instead. */
  logoPath: z.string().min(1).max(500).optional(),
  /** How wide the logo is drawn in the header, in pixels. The height follows the image. */
  logoWidth: z.number().int().min(60).max(320).default(140),
});

export type Brand = z.infer<typeof brandSchema>;

export const DEFAULT_BRAND: Brand = brandSchema.parse({});

/**
 * Reads whatever is stored in `tenant_settings.branding`, which is a free jsonb column and may hold anything at all,
 * including nothing. A church that has never touched it gets the default, and one stored value being wrong falls
 * back for that value alone rather than throwing the whole brand away.
 */
export function parseBrand(value: unknown): Brand {
  if (typeof value !== "object" || value === null) return DEFAULT_BRAND;
  const raw = value as Record<string, unknown>;
  const each = brandSchema.shape;
  const theme = each.theme.safeParse(raw.theme);
  const palette = each.palette.safeParse(raw.palette);
  const typeface = each.typeface.safeParse(raw.typeface);
  const accent = each.accent.safeParse(raw.accent);
  const logoPath = each.logoPath.safeParse(raw.logoPath);
  const logoWidth = each.logoWidth.safeParse(raw.logoWidth);
  return {
    theme: theme.success ? theme.data : DEFAULT_BRAND.theme,
    palette: palette.success ? palette.data : DEFAULT_BRAND.palette,
    typeface: typeface.success ? typeface.data : DEFAULT_BRAND.typeface,
    accent: accent.success ? accent.data : DEFAULT_BRAND.accent,
    ...(logoPath.success && logoPath.data !== undefined ? { logoPath: logoPath.data } : {}),
    logoWidth: logoWidth.success ? logoWidth.data : DEFAULT_BRAND.logoWidth,
  };
}

/** The sRGB channels of a hex colour, 0 to 255. */
function channels(hex: string): [number, number, number] {
  const value = hex.slice(1);
  const full =
    value.length === 3
      ? value
          .split("")
          .map((c) => c + c)
          .join("")
      : value;
  return [
    Number.parseInt(full.slice(0, 2), 16),
    Number.parseInt(full.slice(2, 4), 16),
    Number.parseInt(full.slice(4, 6), 16),
  ];
}

/** Relative luminance, WCAG 2.1 definition. */
export function luminance(hex: string): number {
  const [r, g, b] = channels(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)];
  const [light, dark] = x > y ? [x, y] : [y, x];
  return (light + 0.05) / (dark + 0.05);
}

/**
 * Black or white, whichever reads on the given colour. A church picks an accent without thinking about the label on
 * top of it, so the theme decides that rather than asking.
 */
export function readableOn(hex: string): "#000000" | "#ffffff" {
  return contrastRatio(hex, "#ffffff") >= contrastRatio(hex, "#000000") ? "#ffffff" : "#000000";
}

/**
 * Whether an accent is legible as text on this palette, at 4.5:1. A church may still choose it — it is their brand,
 * and it is fine on a button, where the theme picks the label colour — but the theme then uses the palette's own text
 * colour for links rather than publishing text nobody can read.
 *
 * A link appears on two grounds, not one: the page ground, and the card surface it sits on inside a tile. On the dark
 * palette those pull in opposite directions — the ground is black and the surface is lighter — so an accent can pass
 * on the page and fail inside a card. It has to hold on both, or the check passes a link nobody can read.
 */
export function accentReadsAsText(accent: string, palette: Palette): boolean {
  return (
    contrastRatio(accent, PALETTE_GROUNDS[palette]) >= 4.5 &&
    contrastRatio(accent, PALETTE_SURFACES[palette]) >= 4.5
  );
}

/**
 * Each palette's reading ground and card surface. These mirror `--pw-bg` and `--pw-surface` in the standard theme's
 * stylesheet (`packages/theme-standard/theme.css`); if one changes there it changes here, and `brand.test.ts` reads
 * the stylesheet to say so. They drifted once already —
 * quiet and dark were both stale, and the only test on them checked that they were valid hex.
 */
export const PALETTE_GROUNDS: Record<Palette, string> = {
  quiet: "#f5f5f7",
  warm: "#f5efe4",
  mono: "#ffffff",
  dark: "#000000",
};

export const PALETTE_SURFACES: Record<Palette, string> = {
  quiet: "#ffffff",
  warm: "#fffaf2",
  mono: "#f5f5f7",
  dark: "#1d1d1f",
};
